import classNames from 'clsx';
import { ReactComponent as IconClose } from 'img/icons/close_s__2.svg';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dmrMidasRenew } from 'reactApp/appHelpers/featuresHelpers/features/dmrMidas';
import { BillingActions } from 'reactApp/modules/billing/billing.module';
import { BillingSelectors } from 'reactApp/modules/billing/billing.selectors';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { PaymentWindowType } from 'reactApp/modules/payment/payment.analytics';
import { cheapestProductIDs } from 'reactApp/modules/products/products.const';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { RootState } from 'reactApp/store';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { DMR } from 'reactApp/ui/DMRFrame/DMR';
import { PaymentFooter } from 'reactApp/ui/PaymentFooter/PaymentFooter';
import { createGaSender } from 'reactApp/utils/ga';
import { getPeriodName } from 'reactApp/utils/Period';

import styles from './RenewSubscriptionModal.css';

interface Props {
    subscriptionId: string;
    onClose?: () => void;
    onSuccess?: () => void;
    isMobile?: boolean;
    isGrace?: boolean;
}

const sendBillingGa = createGaSender('billing');

export const RenewSubscriptionModal = memo(({ subscriptionId, onClose, onSuccess, isMobile, isGrace }: Props): ReactElement | null => {
    const dispatch = useDispatch();

    const subscription = useSelector((state: RootState) => BillingDataSelectors.getSubscriptionsById(state, subscriptionId));
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, subscription?.productId));

    const renewLink = useSelector(BillingSelectors.getPaymentRenewLink);
    const { isLoading, hasError } = useSelector(BillingSelectors.getLoadingState);

    const [frameHeight, setFrameHeight] = useState<number>(0);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);

    const isMidas = dmrMidasRenew;
    const isMobileMidas = isMobile && dmrMidasRenew;

    const eventCategory = isMidas ? 'midasRenewBuyModal' : 'renewBuyMoodal';

    const description =
        isGrace && product
            ? `Доплата за подписку +${product.space.space} ${product.space.units} на ${getPeriodName(product.period, false, true)} `
            : undefined;

    const renewSubscriptions = useCallback((): void => {
        dispatch(BillingActions.renewSubscription({ id: subscriptionId, isMidas, isMobile, description }));
    }, [dispatch, subscriptionId, isMidas, description, isMobile]);

    const resetLink = useCallback((): void => {
        dispatch(BillingActions.resetLink());
    }, [dispatch]);

    const handleOnRenewClose = useCallback((): void => {
        sendBillingGa('renew-close');
        dispatch(BillingActions.resetLink());
        onClose?.();
    }, [dispatch, onClose]);

    const handleOnSuccessRenew = useCallback((): void => {
        sendBillingGa('renew-success');

        setIsSuccess(true);
        onSuccess?.();
    }, [onSuccess]);

    const handleOnErrorRenew = useCallback((): void => {
        sendBillingGa('renew-error');
    }, []);

    const handleResize = useCallback(
        (data: { height?: number }): void => {
            const newHeight = data?.height;

            if (newHeight && newHeight !== frameHeight) {
                setFrameHeight(newHeight);
            }
        },
        [frameHeight]
    );

    const modalHeader = useMemo(() => {
        if (!subscription) {
            return '';
        }

        if (isSuccess && !isGrace) {
            // tempexp_16598-start
            if (Object.values(cheapestProductIDs).includes(subscription.productId)) {
                return <div>Вы продлили тариф Без рекламы</div>;
            }
            // tempexp_16598-end

            return <div>Вы продлили тариф на&nbsp;{subscription?.space?.value}</div>;
        }

        if (isGrace) {
            return <div>Оплата подписки Mail Space&nbsp;{subscription?.space?.value}</div>;
        }

        // tempexp_16598-start
        if (Object.values(cheapestProductIDs).includes(subscription.productId)) {
            return <div>Продление периода тарифа Без рекламы</div>;
        }
        // tempexp_16598-end

        return <div>Продление периода тарифа на&nbsp;{subscription?.space?.value}</div>;
    }, [isSuccess, subscription, isGrace]);

    useEffect(() => {
        sendBillingGa('renew-show');
    }, []);

    if (!subscription) {
        return null;
    }

    return (
        <Dialog
            id="tariff-renew"
            open
            size="larger"
            header={isMobileMidas ? '' : modalHeader}
            onCancel={handleOnRenewClose}
            mod={isMobile ? 'mobile' : 'frame'}
            closeOnDimmerClick={false}
            closeOnEscape={false}
            dimmer={!isMobile}
            showCloseIcon={!isMobileMidas}
            disableScrollOnBody
            closable
            disableDarkTheme
            className={classNames({
                [styles.mobileModal]: isMobile,
                [styles.midasModal]: isMidas,
            })}
        >
            <div
                className={classNames(styles.root, {
                    [styles.root_mobile]: isMobile,
                    [styles.root_midasMobile]: isMobileMidas,
                })}
            >
                {isMobileMidas && (
                    <div className={styles.headerBlock}>
                        <div className={styles.header}>{modalHeader}</div>
                        <div className={styles.closeIcon} onClick={handleOnRenewClose}>
                            <IconClose />
                        </div>
                    </div>
                )}
                <div style={frameHeight && !isMidas ? { height: frameHeight } : {}} className={classNames(styles.dmrContainer)}>
                    <DMR
                        buySubscription={renewSubscriptions}
                        resetSubscription={resetLink}
                        subscription={product}
                        onCancel={handleOnRenewClose}
                        onSuccess={handleOnSuccessRenew}
                        isLoading={isLoading}
                        hasError={hasError}
                        onError={handleOnErrorRenew}
                        onResize={handleResize}
                        checkoutLink={renewLink}
                        className={styles.dmr}
                        disableLoaderCentering
                        eventCategory={eventCategory}
                        size={isMobile ? 'medium' : 'larger'}
                        skin="vkpay"
                        paySource="renew"
                        windowType={PaymentWindowType.renew}
                        isMidas={isMidas}
                        showCloseIcon={!isMobileMidas}
                    />
                </div>
                <div className={styles.footer}>
                    <PaymentFooter productId={subscription?.productId} subscription={subscription} isMobile={isMobile} product={product} />
                </div>
            </div>
        </Dialog>
    );
});

RenewSubscriptionModal.displayName = 'RenewSubscriptionModal';
